import { getOwnerBalanceThunk } from '../store/slices/paymentsSlice';

/**
 * Сортирует массив объектов по дате в формате "DD.MM.YYYY".
 *
 * @param {Array<Object>} items - Массив объектов, где каждый объект содержит поле `date` (строка в формате "DD.MM.YYYY").
 * @param {'newFirst' | 'oldFirst'} [order='newFirst'] - Порядок сортировки:
 *  - `'newFirst'` — сначала новые даты (по убыванию)
 *  - `'oldFirst'` — сначала старые даты (по возрастанию)
 *
 * @returns {Array<Object>} Новый отсортированный массив.
 *
 * @example
 * const data = [
 *   { id: 1, date: '12.04.2024' },
 *   { id: 2, date: '05.02.2023' },
 *   { id: 3, date: '21.06.2024' },
 * ];
 *
 * const sorted = sortByDate(data, 'newFirst');
 * // => [{id: 3, ...}, {id: 1, ...}, {id: 2, ...}]
 */
export function sortByDate(items, order = 'newFirst') {
  console.log(items);
  return [...items]?.sort((a, b) => {
    const [dayA, monthA, yearA] = a.date.split('.').map(Number);
    const [dayB, monthB, yearB] = b.date.split('.').map(Number);

    const dateA = new Date(yearA, monthA - 1, dayA);
    const dateB = new Date(yearB, monthB - 1, dayB);

    return order === 'newFirst' ? dateB - dateA : dateA - dateB;
  });
}
/**
 * @param {number} value - число для форматирования
 * @param {'+' | '-' | ''} [sign] - необязательный знак, добавляемый в начало строки
 * @returns {string} - форматированная строка
 */

export function formatCurrency(value, sign = '') {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';

  // Округляем до двух знаков (чтобы сохранить копейки)
  const [integerPart, decimalPart] = value.toFixed(2).split('.');

  // Форматируем целую часть с пробелами между тысячами
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  // Собираем итоговую строку
  const formatted =
    decimalPart && decimalPart !== '00'
      ? `${formattedInteger},${decimalPart}`
      : `${formattedInteger}`;

  // Добавляем знак, если он есть
  return sign ? `${sign}${formatted}` : formatted;
}

/**
 * Применяет процентную скидку к числу
 * @param {number} value - исходная цена
 * @param {number} discountPercent - процент скидки
 * @returns {number} - цена после скидки
 */
export function applyDiscount(value, discountPercent) {
  return value * (1 - discountPercent / 100);
}

/**
 * Преобразует строку суммы в формат, подходящий для CloudPayments.
 *
 * CloudPayments требует число с точкой в качестве десятичного разделителя.
 * Примеры:
 *   "91 901,52" -> 91901.52
 *   "1200" -> 1200
 *   500 -> 500
 *
 * Если значение не является числом или строкой, возвращает null.
 *
 * @param {string|number} amount - Сумма, извлечённая из popupProps
 * @returns {number|null} - Число в формате CP или null, если невозможно преобразовать
 */

export function parseAmountForCP(amount) {
  if (typeof amount === 'number') return amount; // уже число
  if (typeof amount !== 'string') return null; // не строка и не число — не преобразуем

  // Убираем пробелы и заменяем запятую на точку
  const cleaned = amount.replace(/\s/g, '').replace(',', '.');
  const parsed = Number(cleaned);

  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Вычисляет суммарное количество `qty` в массиве объектов.
 *
 * @param {Array<Object>} items - Массив объектов, каждый из которых может содержать поле `qty`.
 * @param {number} [items[].qty] - Количество, которое нужно суммировать. Если поле отсутствует, считается как 0.
 * @returns {number} Сумма всех `qty` в массиве. Если массив пустой или не передан, возвращается 0.
 *
 * @example
 * const data = [
 *   { qty: 10 },
 *   { qty: 5 },
 *   { qty: 15 }
 * ];
 *
 * const total = getTotalQty(data);
 * console.log(total); // 30
 */
export function getTotalQty(items) {
  if (!Array.isArray(items) || items.length === 0) return 0;
  return items.reduce((total, item) => total + (item.qty || 0), 0);
}

/**
 * Преобразует число, записанное в русском формате (с пробелами и запятой),
 * в обычное число JavaScript.
 *
 * Например:
 *  - "48 503,58" → 48503.58
 *  - "1 000" → 1000
 *
 * @param {string} str - Строка с числом в русском формате (может содержать пробелы и запятую).
 * @returns {number} Числовое значение, преобразованное в формат JavaScript.
 *                   Возвращает NaN, если входное значение не является строкой
 *                   или не может быть преобразовано в число.
 */
export function parseRuNumber(str) {
  if (typeof str !== 'string') return NaN;
  return Number(str.replace(/\s/g, '').replace(',', '.'));
}
/**
 * Преобразует строку вида "07.09.2025" в Date.
 *
 * @param {string} dateStr - Дата в формате DD.MM.YYYY.
 * @returns {Date} - Объект даты.
 */
function parseDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return new Date(0);

  const [day, month, year] = dateStr.split('.');
  return new Date(Number(year), Number(month) - 1, Number(day));
}

/**
 * Сортирует массив заказов по дате (новые сверху — старые снизу).
 *
 * @param {Array<Object>} orders - Массив объектов заказов.
 * @param {string} [field="creationDate"] - Поле, по которому сортировать ("creationDate" или "plan_delivery_date").
 * @returns {Array<Object>} - Новый отсортированный массив.
 *
 * @example
 * sortOrdersByDateDesc(orders);
 * sortOrdersByDateDesc(orders, "plan_delivery_date");
 */
export function sortOrdersByDateDesc(orders, field = 'creationDate') {
  if (!Array.isArray(orders)) return [];

  return [...orders].sort((a, b) => {
    const dateA = parseDate(a[field]);
    const dateB = parseDate(b[field]);

    // более новая дата → выше
    return dateB - dateA;
  });
}

export const getTimesByDate = (selectedDate, data) => {
  console.log(selectedDate, data);
  const step = data.find((item) => item.date === selectedDate);
  console.log(step);
  const found = [];
  step?.times?.forEach((element) => {
    found.push({
      value: `${element.time_from}-${element.time_to}`,
      label: `${element.time_from}-${element.time_to}`,
    });
  });
  console.log(found);
  return found || [];
};

/**
 * Ожидает обновления баланса пользователя, периодически повторно запрашивая его.
 *
 * Функция делает паузу (delay), затем вызывает getOwnerBalanceThunk(userId).
 * Если баланс изменился по сравнению с предыдущим значением — считается, что
 * обновление произошло успешно. В противном случае делает повторную попытку.
 *
 * @async
 * @function waitAndUpdateBalance
 *
 * @param {number|string} userId — ID пользователя, для которого нужно обновить баланс.
 * @param {number} [attempts=5] — Максимальное количество попыток проверки обновления.
 * @param {number} [delay=2000] — Задержка между попытками (мс).
 *
 * @returns {Promise<boolean>}
 * Возвращает:
 * - `true`, если баланс обновился раньше, чем закончились попытки;
 * - `false`, если обновление так и не произошло.
 *
 * @example
 * const updated = await waitAndUpdateBalance(123);
 * if (updated) {
 *   console.log("Баланс обновлен!");
 * } else {
 *   console.log("Баланс не успел обновиться.");
 * }
 */
export async function waitAndUpdateBalance(userId, dispatch, attempts = 5, delay = 2000) {
  let previousBalance = null;

  // создаём массив промисов для всех попыток
  const promises = Array.from(
    { length: attempts },
    (_, i) =>
      new Promise((resolve) => {
        setTimeout(async () => {
          try {
            const newBalance = await dispatch(getOwnerBalanceThunk(userId)).unwrap();
            console.log('обновление баланса', i);
            resolve(newBalance);
          } catch {
            resolve(null); // если ошибка, просто возвращаем null
          }
        }, i * delay); // каждая попытка через delay*i мс
      })
  );

  const results = await Promise.all(promises);

  // проверяем результаты через методы массива вместо for...of
  const balanceChanged = results.some((newBalance) => {
    if (previousBalance !== null && newBalance !== previousBalance) {
      return true; // some остановит перебор при true
    }
    if (newBalance !== null) previousBalance = newBalance;
    return false;
  });

  return balanceChanged;
}

/**
 * Преобразует исходный массив данных ГРМ в формат для отправки в API.
 *
 * @param {Array} data - Исходный массив объектов (содержащий grm и qty).
 * @returns {Object} Объект с массивом grms, где ключи приведены к нужному формату.
 *
 * @example
 * const input = [{ grm: "GRM000346", qty: 25, ... }];
 * const output = transformGrms(input);
 * // Результат: { grms: [{ num: "GRM000346", qty: 25 }] }
 */
export function transformGrms(data) {
  if (!Array.isArray(data)) return { grms: [] };
  console.log('function work with data: ', data);
  return {
    grms: data
      // Фильтруем, чтобы qty всегда был > 0 (согласно твоим требованиям)
      .filter((item) => item.qty > 0)
      .map((item) => ({
        num: item.grm,
        qty: item.qty,
      })),
  };
}

/**
 * Функция извлекает Имя и Фамилию из строки любой полноты.
 * @param {string} fullName - Строка вида "Фамилия Имя Отчество" или "Имя Фамилия"
 * @returns {string} - Строка в формате "Имя Фамилия"
 */
export function getFirstAndLastName(fullName) {
  if (!fullName) return '';

  // 1. Разбиваем строку на массив слов, убирая лишние пробелы
  const parts = fullName.trim().split(/\s+/);

  // 2. Если введено 3 и более слов (Фамилия Имя Отчество...),
  // берем 2-е слово как Имя и 1-е как Фамилию
  if (parts.length >= 3) {
    const [lastName, firstName] = parts;
    return `${firstName} ${lastName}`;
  }

  // 3. Если 2 слова (Имя Фамилия), возвращаем их как есть.
  // Блок else не нужен, так как предыдущий if сделал return
  if (parts.length === 2) {
    return `${parts[0]} ${parts[1]}`;
  }

  // 4. Если введено одно слово или иные случаи
  return parts[0] || '';
}

export function areLocationsSimilar(item, reference) {
  // Подъезд
  const entranceMatch =
    (item.entrance === 0 && reference.entrance === 0) ||
    item.entrance === reference.entrance;

  // Этаж
  const floorMatch =
    (item.floor === 0 && reference.floor === 0) || item.floor === reference.floor;

  // Домофон
  const intercomMatch =
    (!item.intercom && !reference.intercom) || item.intercom === reference.intercom;

  // Тип лифта
  const elevatorMatch =
    (!item.elevator_type && !reference.elevator_type) ||
    item.elevator_type === reference.elevator_type;

  // Полный адрес (строгое сравнение)
  const addressMatch = item.full_address === reference.full_address;

  return entranceMatch && floorMatch && intercomMatch && elevatorMatch && addressMatch;
}

/**
 * Группирует массив по doc_no и считает:
 * - количество услуг (servicesCount)
 * - общую сумму (totalAmount)
 *
 * @param {Array<Object>} data - исходный массив
 * @param {string} data[].doc_no - номер документа
 * @param {string} [data[].service] - название услуги (если есть — учитывается)
 * @param {number|string} [data[].amount] - стоимость услуги
 *
 * @returns {Array<Object>} массив сгруппированных документов
 * @returns {string} returns[].doc_no - номер документа
 * @returns {number} returns[].servicesCount - количество услуг
 * @returns {number} returns[].totalAmount - общая сумма услуг
 *
 * @example
 * const result = transformDocs(data);
 * // [
 * //   {
 * //     doc_no: "00036-0000328",
 * //     servicesCount: 2,
 * //     totalAmount: 623
 * //   }
 * // ]
 */
export function transformDocs(data) {
  const resultMap = {};

  (data ?? []).forEach((item) => {
    const docNo = item.doc_no;

    // если документа ещё нет — создаём
    if (!resultMap[docNo]) {
      resultMap[docNo] = {
        doc_no: docNo,
        servicesCount: 0,
        totalAmount: 0,
      };
    }

    // учитываем только элементы с услугами
    if (item.service) {
      resultMap[docNo].servicesCount += 1;

      // приводим amount к числу (на случай строки)
      resultMap[docNo].totalAmount += Number(item.amount) || 0;
    }
  });

  // превращаем объект в массив
  return Object.values(resultMap);
}

/**
 * Сортирует банковские транзакции по дате и времени.
 *
 * Новые транзакции отображаются первыми.
 *
 * Формат даты:
 * - DD.MM.YYYY
 *
 * Формат времени:
 * - HH:mm
 *
 * Пример:
 * {
 *   date: '18.05.2026',
 *   time: '14:30'
 * }
 *
 * @param {Array<Object>} transactions - Массив транзакций.
 * @param {string} transactions[].date - Дата транзакции.
 * @param {string} transactions[].time - Время транзакции.
 *
 * @returns {Array<Object>} Новый отсортированный массив транзакций.
 */
export function sortTransactionsByDate(transactions = []) {
  if (!Array.isArray(transactions)) {
    return [];
  }

  return [...transactions].sort((a, b) => {
    try {
      const dateA = new Date(
        `${a.date.split('.').reverse().join('-')}T${a.time || '00:00'}`
      );

      const dateB = new Date(
        `${b.date.split('.').reverse().join('-')}T${b.time || '00:00'}`
      );

      return dateB.getTime() - dateA.getTime();
    } catch (error) {
      console.error('Ошибка сортировки транзакций:', error);

      return 0;
    }
  });
}

/**
 * Склоняет слово «груз» в зависимости от числительного.
 *
 * @param {number} count - Целое число (количество грузов).
 * @returns {string} Строка в формате "число + правильная форма слова".
 *
 * @example
 * pluralizeCargo(1);   // "1 груз"
 * pluralizeCargo(2);   // "2 груза"
 * pluralizeCargo(5);   // "5 грузов"
 * pluralizeCargo(11);  // "11 грузов"
 * pluralizeCargo(21);  // "21 груз"
 */
export function pluralizeCargo(count) {
  // Получаем абсолютное значение на случай отрицательных чисел
  const absCount = Math.abs(count);

  // Находим остаток от деления на 100 и на 10
  const lastTwoDigits = absCount % 100;
  const lastDigit = absCount % 10;

  // Исключения: от 11 до 14 всегда пишется "грузов" (11 грузов, 12 грузов)
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return `${count} грузов`;
  }

  // Если оканчивается на 1 (кроме 11) -> "груз" (21 груз, 101 груз)
  if (lastDigit === 1) {
    return `${count} груз`;
  }

  // Если оканчивается на 2, 3, 4 (кроме 12, 13, 14) -> "груза" (22 груза, 34 груза)
  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${count} груза`;
  }

  // Во всех остальных случаях (0, 5-9) -> "грузов" (5 грузов, 10 грузов)
  return `${count} грузов`;
}

/**
 * Подсчитывает общее количество копий файлов в массиве.
 *
 * Функция перебирает все элементы массива и суммирует значение
 * свойства `copies` каждого файла. Если значение `copies`
 * отсутствует, равно `null`, `undefined` или не является числом,
 * для такого элемента используется 0.
 *
 * @param {Array<Object>} files - Массив объектов файлов.
 * @param {number|string} files[].copies - Количество копий файла.
 *
 * @returns {number} Общее количество копий всех файлов.
 */
export function getTotalCopies(files) {
  return files.reduce(
    (total, file) => total + (Number(file.copies || file.copies_count) || 0),
    0
  );
}
